export const trigLessons = [
  {
    id: 5,
    title: "Unit Circle & Radian Measure",
    description: "Master the unit circle, convert between degrees and radians, and find exact trig values",
    vocabulary: [
      "Unit Circle",
      "Radian",
      "Degree",
      "Arc Length",
      "Reference Angle",
      "Quadrant",
      "Terminal Side",
      "Standard Position",
      "Coterminal Angles"
    ],
    conceptExplanation: `**The Unit Circle:**
A circle with radius 1 centered at the origin. Used to define trigonometric functions for all angles.

**Radian Measure:**
One radian is the angle formed when the arc length equals the radius.
• Full circle: 2π radians = 360°
• Half circle: π radians = 180°
• Conversion: radians = degrees × (π/180)
• Conversion: degrees = radians × (180/π)

**Key Angle Conversions:**
• 30° = π/6 rad
• 45° = π/4 rad
• 60° = π/3 rad
• 90° = π/2 rad
• 180° = π rad
• 270° = 3π/2 rad
• 360° = 2π rad

**Unit Circle Coordinates:**
For angle θ in standard position, the point on the unit circle is (cos θ, sin θ)

**Special Angles (Exact Values):**
• 0°: (1, 0) → cos = 1, sin = 0
• 30° (π/6): (√3/2, 1/2)
• 45° (π/4): (√2/2, √2/2)
• 60° (π/3): (1/2, √3/2)
• 90° (π/2): (0, 1) → cos = 0, sin = 1

**Reference Angles:**
The acute angle formed with the x-axis. Used to find trig values in all quadrants.

**Quadrant Signs:**
• QI: All positive
• QII: Sin positive, Cos/Tan negative
• QIII: Tan positive, Sin/Cos negative
• QIV: Cos positive, Sin/Tan negative
(Remember: "All Students Take Calculus")

**Coterminal Angles:**
Angles that share the same terminal side. Add or subtract 360° (or 2π radians).`,
    examples: [
      {
        problem: "Convert 135° to radians",
        solution: `Step 1: Use conversion formula
radians = degrees × (π/180)

Step 2: Substitute and simplify
radians = 135 × (π/180)
radians = 135π/180

Step 3: Reduce the fraction
radians = 3π/4

Answer: 3π/4 radians`
      },
      {
        problem: "Find the exact value of sin(5π/6)",
        solution: `Step 1: Identify the quadrant
5π/6 is in Quadrant II (between π/2 and π)

Step 2: Find the reference angle
Reference angle = π - 5π/6 = π/6

Step 3: Find sin(π/6)
sin(π/6) = 1/2

Step 4: Apply quadrant sign
In QII, sine is positive
sin(5π/6) = 1/2

Answer: 1/2`
      },
      {
        problem: "Find a positive and negative coterminal angle for 50°",
        solution: `Step 1: Add 360° for positive coterminal angle
50° + 360° = 410°

Step 2: Subtract 360° for negative coterminal angle
50° - 360° = -310°

Answer: Positive: 410°, Negative: -310°`
      }
    ],
    teacherTip: "The unit circle is your best friend in trigonometry! Memorize the coordinates for 0°, 30°, 45°, 60°, and 90° in the first quadrant—then use reference angles and the 'All Students Take Calculus' rule to find values in other quadrants. For radians, think of π as 180°, so π/2 is half of that (90°), π/4 is a quarter (45°), etc. Draw the unit circle whenever you're stuck!",
    questions: [
      {
        id: 1,
        question: "Convert 90° to radians",
        options: ["π/2", "π", "2π", "π/4"],
        correctAnswer: "π/2",
        solution: "90° × (π/180) = 90π/180 = π/2 radians"
      },
      {
        id: 2,
        question: "Convert 180° to radians",
        options: ["π", "2π", "π/2", "3π/2"],
        correctAnswer: "π",
        solution: "180° × (π/180) = 180π/180 = π radians"
      },
      {
        id: 3,
        question: "Convert 60° to radians",
        options: ["π/3", "π/6", "π/4", "2π/3"],
        correctAnswer: "π/3",
        solution: "60° × (π/180) = 60π/180 = π/3 radians"
      },
      {
        id: 4,
        question: "Convert π/4 radians to degrees",
        options: ["45°", "90°", "30°", "60°"],
        correctAnswer: "45°",
        solution: "(π/4) × (180/π) = 180/4 = 45°"
      },
      {
        id: 5,
        question: "Convert 2π/3 radians to degrees",
        options: ["120°", "60°", "90°", "150°"],
        correctAnswer: "120°",
        solution: "(2π/3) × (180/π) = 360/3 = 120°"
      },
      {
        id: 6,
        question: "What is sin(30°)?",
        options: ["1/2", "√3/2", "√2/2", "1"],
        correctAnswer: "1/2",
        solution: "From the unit circle, sin(30°) = sin(π/6) = 1/2"
      },
      {
        id: 7,
        question: "What is cos(60°)?",
        options: ["1/2", "√3/2", "√2/2", "0"],
        correctAnswer: "1/2",
        solution: "From the unit circle, cos(60°) = cos(π/3) = 1/2"
      },
      {
        id: 8,
        question: "What is sin(45°)?",
        options: ["√2/2", "1/2", "√3/2", "1"],
        correctAnswer: "√2/2",
        solution: "From the unit circle, sin(45°) = sin(π/4) = √2/2"
      },
      {
        id: 9,
        question: "What is cos(45°)?",
        options: ["√2/2", "1/2", "√3/2", "0"],
        correctAnswer: "√2/2",
        solution: "From the unit circle, cos(45°) = cos(π/4) = √2/2"
      },
      {
        id: 10,
        question: "What is sin(90°)?",
        options: ["1", "0", "√2/2", "1/2"],
        correctAnswer: "1",
        solution: "From the unit circle, sin(90°) = sin(π/2) = 1"
      },
      {
        id: 11,
        question: "What is cos(0°)?",
        options: ["1", "0", "√2/2", "1/2"],
        correctAnswer: "1",
        solution: "From the unit circle, cos(0°) = 1"
      },
      {
        id: 12,
        question: "In which quadrant is 150° located?",
        options: ["QII", "QI", "QIII", "QIV"],
        correctAnswer: "QII",
        solution: "150° is between 90° and 180°, so it's in Quadrant II"
      },
      {
        id: 13,
        question: "In which quadrant is 5π/4 located?",
        options: ["QIII", "QII", "QI", "QIV"],
        correctAnswer: "QIII",
        solution: "5π/4 = 225°, which is between 180° and 270°, so it's in Quadrant III"
      },
      {
        id: 14,
        question: "What is the reference angle for 150°?",
        options: ["30°", "60°", "45°", "150°"],
        correctAnswer: "30°",
        solution: "150° is in QII. Reference angle = 180° - 150° = 30°"
      },
      {
        id: 15,
        question: "What is the reference angle for 210°?",
        options: ["30°", "60°", "45°", "150°"],
        correctAnswer: "30°",
        solution: "210° is in QIII. Reference angle = 210° - 180° = 30°"
      },
      {
        id: 16,
        question: "Find sin(150°)",
        options: ["1/2", "-1/2", "√3/2", "-√3/2"],
        correctAnswer: "1/2",
        solution: "150° is in QII where sine is positive. Reference angle is 30°. sin(150°) = sin(30°) = 1/2"
      },
      {
        id: 17,
        question: "Find cos(150°)",
        options: ["-√3/2", "√3/2", "1/2", "-1/2"],
        correctAnswer: "-√3/2",
        solution: "150° is in QII where cosine is negative. Reference angle is 30°. cos(150°) = -cos(30°) = -√3/2"
      },
      {
        id: 18,
        question: "Find sin(π)",
        options: ["0", "1", "-1", "√2/2"],
        correctAnswer: "0",
        solution: "π radians = 180°. The point on the unit circle is (-1, 0). sin(π) = 0"
      },
      {
        id: 19,
        question: "Find cos(π)",
        options: ["-1", "1", "0", "√2/2"],
        correctAnswer: "-1",
        solution: "π radians = 180°. The point on the unit circle is (-1, 0). cos(π) = -1"
      },
      {
        id: 20,
        question: "Find a positive coterminal angle for 30°",
        options: ["390°", "330°", "-330°", "60°"],
        correctAnswer: "390°",
        solution: "Add 360°: 30° + 360° = 390°"
      },
      {
        id: 21,
        question: "Find a negative coterminal angle for 45°",
        options: ["-315°", "-45°", "405°", "-405°"],
        correctAnswer: "-315°",
        solution: "Subtract 360°: 45° - 360° = -315°"
      },
      {
        id: 22,
        question: "In which quadrant is sine positive and cosine negative?",
        options: ["QII", "QI", "QIII", "QIV"],
        correctAnswer: "QII",
        solution: "In Quadrant II, sine is positive and cosine is negative (All Students Take Calculus)"
      },
      {
        id: 23,
        question: "In which quadrant are both sine and cosine negative?",
        options: ["QIII", "QII", "QI", "QIV"],
        correctAnswer: "QIII",
        solution: "In Quadrant III, both sine and cosine are negative (only tangent is positive)"
      },
      {
        id: 24,
        question: "What is the arc length of a circle with radius 5 and central angle π/3?",
        options: ["5π/3", "5π", "π/3", "15π"],
        correctAnswer: "5π/3",
        solution: "Arc length = radius × angle (in radians) = 5 × π/3 = 5π/3"
      },
      {
        id: 25,
        question: "Convert 270° to radians",
        options: ["3π/2", "π/2", "2π", "π"],
        correctAnswer: "3π/2",
        solution: "270° × (π/180) = 270π/180 = 3π/2 radians"
      },
      {
        id: 26,
        question: "Find sin(7π/6)",
        options: ["-1/2", "1/2", "-√3/2", "√3/2"],
        correctAnswer: "-1/2",
        solution: "7π/6 is in QIII where sine is negative. Reference angle is π/6. sin(7π/6) = -sin(π/6) = -1/2"
      },
      {
        id: 27,
        question: "Find cos(5π/4)",
        options: ["-√2/2", "√2/2", "-1/2", "1/2"],
        correctAnswer: "-√2/2",
        solution: "5π/4 is in QIII where cosine is negative. Reference angle is π/4. cos(5π/4) = -cos(π/4) = -√2/2"
      },
      {
        id: 28,
        question: "What is tan(45°)?",
        options: ["1", "√2/2", "√3", "1/2"],
        correctAnswer: "1",
        solution: "tan(45°) = sin(45°)/cos(45°) = (√2/2)/(√2/2) = 1"
      },
      {
        id: 29,
        question: "What is the period of the sine function?",
        options: ["2π", "π", "π/2", "4π"],
        correctAnswer: "2π",
        solution: "The sine function repeats every 2π radians (360°). Period = 2π"
      },
      {
        id: 30,
        question: "Which angle is NOT coterminal with 60°?",
        options: ["240°", "420°", "-300°", "780°"],
        correctAnswer: "240°",
        solution: "Coterminal angles differ by multiples of 360°. 420° = 60° + 360° ✓, -300° = 60° - 360° ✓, 780° = 60° + 720° ✓. But 240° = 60° + 180°, not a multiple of 360°."
      }
    ]
  },
  {
    id: 6,
    title: "Trigonometric Functions & Graphs",
    description: "Graph sine, cosine, and tangent functions with transformations, amplitude, period, and phase shift",
    vocabulary: [
      "Amplitude",
      "Period",
      "Phase Shift",
      "Vertical Shift",
      "Midline",
      "Frequency",
      "Sinusoidal",
      "Asymptote",
      "Maximum",
      "Minimum"
    ],
    conceptExplanation: `**General Form of Sinusoidal Functions:**
f(x) = A sin[B(x - C)] + D  or  f(x) = A cos[B(x - C)] + D

Where:
• |A| = amplitude (distance from midline to max/min)
• Period = 2π/|B|
• C = phase shift (horizontal shift)
• D = vertical shift (midline)

**Parent Functions:**
• y = sin(x): starts at (0,0), period 2π, amplitude 1
• y = cos(x): starts at (0,1), period 2π, amplitude 1
• y = tan(x): period π, vertical asymptotes at x = π/2 + nπ

**Key Features of y = sin(x):**
• Domain: all real numbers
• Range: [-1, 1]
• Period: 2π
• Zeros: x = nπ (where n is an integer)
• Maximum: 1 at x = π/2 + 2nπ
• Minimum: -1 at x = 3π/2 + 2nπ

**Key Features of y = cos(x):**
• Domain: all real numbers
• Range: [-1, 1]
• Period: 2π
• Zeros: x = π/2 + nπ
• Maximum: 1 at x = 2nπ
• Minimum: -1 at x = π + 2nπ

**Key Features of y = tan(x):**
• Domain: all real numbers except x = π/2 + nπ
• Range: all real numbers
• Period: π
• Zeros: x = nπ
• Vertical asymptotes: x = π/2 + nπ

**Transformations:**
• A < 0: reflection over x-axis
• B > 1: horizontal compression (shorter period)
• 0 < B < 1: horizontal stretch (longer period)
• C > 0: shift right
• C < 0: shift left
• D > 0: shift up
• D < 0: shift down`,
    examples: [
      {
        problem: "Find the amplitude, period, and phase shift of f(x) = 3sin(2x - π) + 1",
        solution: `Step 1: Rewrite in standard form
f(x) = 3sin[2(x - π/2)] + 1

Step 2: Identify A, B, C, D
A = 3, B = 2, C = π/2, D = 1

Step 3: Calculate features
Amplitude = |A| = |3| = 3
Period = 2π/|B| = 2π/2 = π
Phase shift = C = π/2 (right)
Vertical shift = D = 1 (up)
Midline: y = 1

Answer: Amplitude = 3, Period = π, Phase shift = π/2 right, Vertical shift = 1 up`
      },
      {
        problem: "Graph y = -2cos(x) + 1",
        solution: `Step 1: Identify transformations
A = -2 (amplitude 2, reflected over x-axis)
B = 1 (period = 2π/1 = 2π)
C = 0 (no phase shift)
D = 1 (shift up 1)

Step 2: Find key features
Midline: y = 1
Maximum: y = 1 + 2 = 3 (at x = π, 3π, ...)
Minimum: y = 1 - 2 = -1 (at x = 0, 2π, ...)

Step 3: Plot key points
(0, -1), (π/2, 1), (π, 3), (3π/2, 1), (2π, -1)

Step 4: Sketch smooth curve through points

The graph is a cosine curve reflected over the x-axis, with amplitude 2 and midline y = 1.`
      },
      {
        problem: "Find the equation of a sine function with amplitude 4, period π, and phase shift π/4 right",
        solution: `Step 1: Use general form
f(x) = A sin[B(x - C)] + D

Step 2: Find A from amplitude
Amplitude = 4, so A = 4 (or -4)
Use A = 4

Step 3: Find B from period
Period = 2π/B = π
B = 2π/π = 2

Step 4: Find C from phase shift
Phase shift = π/4 right, so C = π/4

Step 5: Assume no vertical shift
D = 0

Answer: f(x) = 4sin[2(x - π/4)] or f(x) = 4sin(2x - π/2)`
      }
    ],
    teacherTip: "When graphing trig functions, always start by finding the five key points for one period. For sine: start, max, middle, min, end. For cosine: max, middle, min, middle, max. The period tells you how wide one cycle is, and the amplitude tells you how tall. Remember: B affects the period (2π/B), not the amplitude! And always check if there's a negative sign in front—that flips the graph upside down.",
    questions: [
      {
        id: 1,
        question: "What is the amplitude of y = 5sin(x)?",
        options: ["5", "1", "2π", "0"],
        correctAnswer: "5",
        solution: "Amplitude = |A| = |5| = 5"
      },
      {
        id: 2,
        question: "What is the period of y = sin(2x)?",
        options: ["π", "2π", "4π", "π/2"],
        correctAnswer: "π",
        solution: "Period = 2π/|B| = 2π/2 = π"
      },
      {
        id: 3,
        question: "What is the period of y = cos(x/2)?",
        options: ["4π", "2π", "π", "π/2"],
        correctAnswer: "4π",
        solution: "Period = 2π/|B| = 2π/(1/2) = 4π"
      },
      {
        id: 4,
        question: "What is the amplitude of y = -3cos(x)?",
        options: ["3", "-3", "1", "2π"],
        correctAnswer: "3",
        solution: "Amplitude = |A| = |-3| = 3 (amplitude is always positive)"
      },
      {
        id: 5,
        question: "What is the midline of y = sin(x) + 4?",
        options: ["y = 4", "y = 0", "y = 1", "y = 5"],
        correctAnswer: "y = 4",
        solution: "Midline = D = 4, so y = 4"
      },
      {
        id: 6,
        question: "What is the range of y = 2sin(x)?",
        options: ["[-2, 2]", "[-1, 1]", "[0, 2]", "[-2, 0]"],
        correctAnswer: "[-2, 2]",
        solution: "Range = [D - |A|, D + |A|] = [0 - 2, 0 + 2] = [-2, 2]"
      },
      {
        id: 7,
        question: "What is the range of y = cos(x) - 3?",
        options: ["[-4, -2]", "[-3, -1]", "[-1, 1]", "[-2, 2]"],
        correctAnswer: "[-4, -2]",
        solution: "Range = [D - |A|, D + |A|] = [-3 - 1, -3 + 1] = [-4, -2]"
      },
      {
        id: 8,
        question: "What transformation does y = sin(x - π/2) represent?",
        options: ["Shift right π/2", "Shift left π/2", "Shift up π/2", "Shift down π/2"],
        correctAnswer: "Shift right π/2",
        solution: "Phase shift = C = π/2. Since it's (x - π/2), shift right π/2"
      },
      {
        id: 9,
        question: "What transformation does y = sin(x + π) represent?",
        options: ["Shift left π", "Shift right π", "Shift up π", "Shift down π"],
        correctAnswer: "Shift left π",
        solution: "Rewrite as sin(x - (-π)). Phase shift = -π, so shift left π"
      },
      {
        id: 10,
        question: "What is the period of y = tan(x)?",
        options: ["π", "2π", "π/2", "4π"],
        correctAnswer: "π",
        solution: "The tangent function has period π (not 2π like sine and cosine)"
      },
      {
        id: 11,
        question: "What is the period of y = tan(2x)?",
        options: ["π/2", "π", "2π", "π/4"],
        correctAnswer: "π/2",
        solution: "For tangent, period = π/|B| = π/2"
      },
      {
        id: 12,
        question: "Where are the vertical asymptotes of y = tan(x)?",
        options: ["x = π/2 + nπ", "x = nπ", "x = π/4 + nπ", "x = 2nπ"],
        correctAnswer: "x = π/2 + nπ",
        solution: "Tangent has vertical asymptotes at x = π/2 + nπ where n is any integer"
      },
      {
        id: 13,
        question: "What is the maximum value of y = 3sin(x) + 2?",
        options: ["5", "3", "2", "1"],
        correctAnswer: "5",
        solution: "Maximum = D + |A| = 2 + 3 = 5"
      },
      {
        id: 14,
        question: "What is the minimum value of y = 3sin(x) + 2?",
        options: ["-1", "2", "-3", "5"],
        correctAnswer: "-1",
        solution: "Minimum = D - |A| = 2 - 3 = -1"
      },
      {
        id: 15,
        question: "Which function has amplitude 2 and period π?",
        options: ["y = 2sin(2x)", "y = 2sin(x)", "y = sin(2x)", "y = 4sin(x)"],
        correctAnswer: "y = 2sin(2x)",
        solution: "Amplitude = |A| = 2, so A = 2. Period = 2π/B = π, so B = 2. Answer: y = 2sin(2x)"
      },
      {
        id: 16,
        question: "What is the phase shift of y = cos(x - π/4)?",
        options: ["π/4 right", "π/4 left", "π/4 up", "π/4 down"],
        correctAnswer: "π/4 right",
        solution: "Phase shift = C = π/4. Since it's (x - π/4), shift right π/4"
      },
      {
        id: 17,
        question: "How does y = -sin(x) compare to y = sin(x)?",
        options: ["Reflected over x-axis", "Reflected over y-axis", "Shifted up", "Shifted right"],
        correctAnswer: "Reflected over x-axis",
        solution: "The negative sign in front reflects the graph over the x-axis"
      },
      {
        id: 18,
        question: "What is the domain of y = sin(x)?",
        options: ["All real numbers", "[-1, 1]", "x ≠ π/2 + nπ", "[0, 2π]"],
        correctAnswer: "All real numbers",
        solution: "Sine function is defined for all real numbers"
      },
      {
        id: 19,
        question: "What is the domain of y = tan(x)?",
        options: ["All real numbers except x = π/2 + nπ", "All real numbers", "[-1, 1]", "x ≠ nπ"],
        correctAnswer: "All real numbers except x = π/2 + nπ",
        solution: "Tangent is undefined at x = π/2 + nπ (vertical asymptotes)"
      },
      {
        id: 20,
        question: "At what value of x does y = sin(x) first reach its maximum in [0, 2π]?",
        options: ["π/2", "0", "π", "2π"],
        correctAnswer: "π/2",
        solution: "sin(x) reaches maximum value of 1 at x = π/2"
      },
      {
        id: 21,
        question: "At what value of x does y = cos(x) first reach its maximum in [0, 2π]?",
        options: ["0", "π/2", "π", "2π"],
        correctAnswer: "0",
        solution: "cos(x) reaches maximum value of 1 at x = 0"
      },
      {
        id: 22,
        question: "What is the amplitude of y = (1/2)cos(x)?",
        options: ["1/2", "2", "1", "π"],
        correctAnswer: "1/2",
        solution: "Amplitude = |A| = |1/2| = 1/2"
      },
      {
        id: 23,
        question: "What is the period of y = sin(4x)?",
        options: ["π/2", "π", "2π", "4π"],
        correctAnswer: "π/2",
        solution: "Period = 2π/|B| = 2π/4 = π/2"
      },
      {
        id: 24,
        question: "Which function is shifted 2 units up from y = sin(x)?",
        options: ["y = sin(x) + 2", "y = sin(x + 2)", "y = 2sin(x)", "y = sin(2x)"],
        correctAnswer: "y = sin(x) + 2",
        solution: "Adding 2 outside the function shifts the graph up 2 units"
      },
      {
        id: 25,
        question: "What is the midline of y = 4cos(x) - 3?",
        options: ["y = -3", "y = 4", "y = 1", "y = -7"],
        correctAnswer: "y = -3",
        solution: "Midline = D = -3, so y = -3"
      },
      {
        id: 26,
        question: "Find the range of y = -2sin(x) + 1",
        options: ["[-1, 3]", "[-3, 1]", "[-2, 2]", "[1, 3]"],
        correctAnswer: "[-1, 3]",
        solution: "Range = [D - |A|, D + |A|] = [1 - 2, 1 + 2] = [-1, 3]"
      },
      {
        id: 27,
        question: "How many complete cycles of y = sin(x) occur in [0, 4π]?",
        options: ["2", "1", "4", "8"],
        correctAnswer: "2",
        solution: "Period of sin(x) is 2π. In interval [0, 4π], there are 4π/2π = 2 complete cycles"
      },
      {
        id: 28,
        question: "What is the frequency of y = sin(3x)?",
        options: ["3", "1/3", "2π/3", "6π"],
        correctAnswer: "3",
        solution: "Frequency = |B| = 3 (number of cycles in 2π)"
      },
      {
        id: 29,
        question: "Which point is on the graph of y = cos(x)?",
        options: ["(0, 1)", "(0, 0)", "(π/2, 1)", "(π, 1)"],
        correctAnswer: "(0, 1)",
        solution: "cos(0) = 1, so the point (0, 1) is on the graph"
      },
      {
        id: 30,
        question: "Which point is on the graph of y = sin(x)?",
        options: ["(0, 0)", "(0, 1)", "(π, 1)", "(π/2, 0)"],
        correctAnswer: "(0, 0)",
        solution: "sin(0) = 0, so the point (0, 0) is on the graph"
      }
    ]
  }
];